<template>
  <div class="article-queries-component">
    <div class="header">
      <div id="form-search-queries" class="card">
        <Search
          v-model="pagination.query"
          @search="onSubmitSearch"
          @clearSearch="onClearSearch"
        />
      </div>


      <div class="actions">
        <div v-if="!insideSidepanel" class="bulk-update-date-queries">
          <button 
            class="btn btn-default" 
            :class="{ 'disabled': isBulkUpdateDisabled }"
            @click="() => !isBulkUpdateDisabled && toggleBulkUpdateForm(true)">
            <i class="fas fa-calendar"></i> {{ $t('admin.bulk_date_update_queries') }}
          </button>
        </div>
        <div v-if="canEditMasterView && !insideSidepanel" class="settings">
          <button :class="{ 'btn btn-default': true }" @click="() => toggleShowEditSettingsForm(true)">
            <i class="fas fa-cog"></i> {{ $t('admin.settings') }}
          </button>
        </div>
        <div v-if="canEditMasterView" class="refresh-queries">
          <button :class="{ 'btn btn-default': true, 'disabled': refreshQueries.isBusy }" @click="onRefreshQueries()">
            <i class="fas fa-sync-alt"></i> {{ $t('admin.refresh_queries') }}
          </button>
        </div>
        <div v-if="!insideSidepanel" class="create-query">
          <button class="btn btn-carma" @click="onAddQuery">
            <i class="fas fa-plus"></i> {{ $t('admin.add_query') }}
          </button>
        </div>
      </div>
    </div>

    <div class="flex-1to1">
      <div class="card mr20 queries-folder-card">
        <load-content :loading="folderStructureIsBusy" class="position-relative w-100 h-100">
          <FolderStructure
            v-if="selectedFolder && selectedFolder.id"
            :data="folderStructure.data || []"
            :location="selectedFolder && selectedFolder.id"
            :allowDeleteFolders="allowDeleteFolders"
            :selectedFolders="selectedFolders"
            @update="onFolderChange"
            @resetPagination="resetPagination"
            @onDeleteRepositoryClick="onDeleteRepositoryClick"
            @onUpdateSelectedFolders="onUpdateSelectedFolders"
            :class="{ small : small }"
            class="flex-1 auto-height folder-area"
          />
        </load-content>
      </div>
      <div class="flex-4 overflow-hidden">
        <div v-if="hasChildProjects" class="d-flex justify-content-between">
          <div class="tabs d-flex overflow-hidden">
            <div v-for="tab in queriesTabs" :key="tab.id" :ref="'tab-' + tab.id" @mouseover="() => getElementTooltip('tab-' + tab.id, tab.name)" v-tooltip="tabTooltipText" class="tab px-3 py-2 cursor-pointer text-truncate" :class="{ 'active': selectedProjectId === tab.id}" @click="() => onChangeTab(tab.id)">{{ tab.name }}</div>
          </div>
          <input-select class="project-dropdown ms-3" :options="queriesProjectOptions" :selectedValue="selectedProjectId" :allowEmpty="false" @change="(value) => onChangeTab(value)">
            <template slot="custom" scope="props">
              <div class="d-flex justify-content-between align-items-center">
                <span>{{ props.scope.option.label }}</span>
                <i v-if="!props.scope.option.default" class="fas fa-thumbtack pin px-2 py-1" :class="{ 'active': isPinActive(props.scope.option.value) }" @click.stop="() => onPinTab(props.scope.option.value)"></i>
              </div>
            </template>
          </input-select>
        </div>
        <div class="card">
          <datatable ref="table" :data="table" :loading="tableLoading" :key="`table-${tableVersion}`"/>
          <div class="loading-next-page" v-if="loadingNextPage"><loader /></div>
        </div>
      </div>
    </div>

    <side-panel v-if="showPanel" :onClose="closePanel">
      <template v-if="showPanel === 'removeQuery'">
        <load-content :loading="isBusy">
          <confirm-delete
              :label="queryModel.name"
              :onDelete="onDeleteQuery"
          />
        </load-content>
      </template>
      <template v-if="showPanel === 'deleteFolder'">
        <confirm-delete
            :label="selectedFolder.label"
            :onDelete="onDeleteFolder"
        />
      </template>
    </side-panel>

    <side-panel v-if="showEditSettingsForm" :onClose="() => toggleShowEditSettingsForm(false)">
      <RefreshQueriesSchedule />
    </side-panel>

    <side-panel v-if="showBulkUpdateForm" :onClose="() => toggleBulkUpdateForm(false)">
      <BulkUpdateQueriesDate 
        :selectedQueries="selectedQueries"
        :selectedFolders="selectedFolders"
        @onBulkUpdateQueriesDateSave="onBulkUpdateQueriesDateSave"
      />
    </side-panel>

    <run-error-modal
      v-if="errorMessage"
      :title="$t('errors.unable_refresh_queries')"
      :text="$t('errors.' + errorMessage)"
      @closeModal="closeModal"
    />

    <confirmation-modal v-if="showConfirmation"
                        :headerText="$t('admin.bulk_update_queries_date.confirm.title')"
                        :message="$t('admin.bulk_update_queries_date.confirm.message', { total_folders: selectedFolders.length, total_queries: selectedQueries.length })"
                        @confirmAction="onConfirmBulkUpdateQueriesDate"
                        @cancelAction="() => toggleBulkUpdateConfirmation(false)" />

    <confirmation-modal v-if="bulkUpdateExcludedResult && bulkUpdateExcludedResult.excluded && bulkUpdateExcludedResult.excluded.length"
                        :headerText="$t('admin.bulk_update_excluded_modal.title')"
                        :showConfirmButton="false"
                        @cancelAction="bulkUpdateExcludedResult = null">
      <template slot="body">
        <p>{{ $t('admin.bulk_update_excluded_modal.message') }}</p>
        <p class="font-weight-bold mt-2">{{ $t('admin.bulk_update_excluded_modal.query_list_header') }}</p>
        <ul class="bulk-update-excluded-list">
          <li v-for="q in bulkUpdateExcludedResult.excluded" :key="q.id">{{ q.id }} - {{ q.name }} <a :href="`/analysis/queries/${projectId}/${q.id}`" target="_blank"><i class="fas fa-external-link-alt"></i></a></li>
        </ul>
        <p class="mt-4">
          <i>{{ $t('admin.bulk_update_excluded_modal.reason') }}</i>
        </p>
      </template>
    </confirmation-modal>

    <side-panel v-if="showForceRefreshConfirm" :onClose="closePanel">
      <load-content :loading="masterViewStatus.isBusy">
        <confirm-action
          :title="confirmRefreshMessages.title"
          :message="confirmRefreshMessages.text"
          :confirmButtonClass="forceRefreshConfirmClasses"
          @confirm="onRefreshQueries(true)"
          @cancel="closePanel"
        >
          <div v-if="canForceRefreshMasterView && !masterViewIsLocked" class="d-flex align-items-center">
            <input-checkbox classDiv="d-flex" v-model="forceRefresh" /><span>{{$t('admin.force_refresh_queries')}}</span>
          </div>
        </confirm-action>
      </load-content>
    </side-panel>
  </div>
</template>

<script>
  import { mapState, mapActions } from 'vuex';
  import moment from 'moment';
  import _ from 'lodash';

  import { QUERIES } from '../../config/const';
  import { getTextWidth } from '@/helpers/string';
  import LoadContent from '../load-content';
  import Loader from "../loader";
  import Search from "../Search.vue";
  import Datatable from '../datatable';
  import SidePanel from '../side-panel';
  import ConfirmDelete from "../confirm-delete";
  import FolderStructure from '../coding-queries/folder_structure';
  import searchMixin from '../../mixins/search';
  import RefreshQueriesSchedule from './queries/refresh-queries-schedule.vue'
  import BulkUpdateQueriesDate from './queries/bulk-update-queries-date.vue'
  import RunErrorModal from '../coding-queries/run_error_modal';
  import ConfirmAction from '../confirm-action';
  import InputCheckbox from '../form-components/input-checkbox';
  import InputSelect from '@/components/form-components/input-select';
  import ConfirmationModal from '@/components/confirmation-modal';

  export default {
    name: 'ArticleGroup',
    mixins: [searchMixin],
    components: {
      datatable: Datatable,
      Search,
      LoadContent,
      SidePanel,
      ConfirmDelete,
      FolderStructure,
      Loader,
      RefreshQueriesSchedule,
      BulkUpdateQueriesDate,
      RunErrorModal,
      ConfirmAction,
      InputCheckbox,
      InputSelect,
      ConfirmationModal
    },
    props: {
      insideSidepanel: {
        type: Boolean,
        require: false,
        default: false,
      },
      allowDeleteFolders: {
        type: Boolean,
        required: false,
        default: true,
      },
      small: {
        type: Boolean,
        required: false,
        default: false,
      },
    },
    computed: {
      ...mapState({
        isInsight: state => state.isInsight,
        insightSessionToken: state => state.insightSessionToken,
        queries: state => !_.isEmpty(_.get(state.queries.queries, 'data.data.data')) ?  state.queries.queries.data.data.data : [],
        queriesPagination: state => !_.isEmpty(_.get(state.queries.queries, 'data.data.pagination')) ? state.queries.queries.data.data.pagination : {},
        isBusy: state => state.queries.queries.isBusy ,
        query: state => state.queryBuilder.query.query,
        user: state => state.user.user.data,
        folderStructure: state => state.queryBuilder.folderStructure.folderStructure,
        refreshQueries: state => state.queryBuilder.masterView.refresh,
        project: state => state.projects.project.data,
        masterViewStatus: state => state.queryBuilder.masterView.status,
        preferences: state => state.user.preferences
      }),
      projectId () {
        return this.$route.params.project_uid || this.$route.params.project_id;
      },

      canEditMasterView () {
        if(!this.user) return false
        return this.user.code_admin || (this.user.code_editor && this.user.permissions.allow_refresh_mv === true);
      },

      canForceRefreshMasterView () {
        if (!this.user) return false;
        return this.canEditMasterView && this.project.enable_mv_force_refresh && this.user.permissions.allow_force_refresh_mv === true;
      },

      masterViewIsLocked () {
        return this.masterViewStatus.data.locked === true;
      },

      forceRefreshConfirmClasses () {
        let cssClasses = 'btn-carma';
        if (this.refreshQueries.isBusy || this.masterViewIsLocked) {
          cssClasses += ' disabled';
        }
        return cssClasses;
      },

      confirmRefreshMessages () {
        const messages = {
          title: this.$t('mv_refresh.confirm.title'),
          text: this.$t('mv_refresh.confirm.text')
        }

        if (this.canForceRefreshMasterView && this.masterViewIsLocked) {
          messages.title = this.$t('mv_refresh.locked.title');
          messages.text = this.$t('mv_refresh.locked.text', {
            date: moment(this.masterViewStatus.data.created_at).format('DD MMM YYYY, HH:mm')
          });
        }

        return messages;
      },

      isBulkUpdateDisabled() {
        const hasFolders = this.selectedFolders && this.selectedFolders.length > 0;
        const hasQueries = this.selectedQueries && this.selectedQueries.length > 0;
        return !hasFolders && !hasQueries;
      },

      folderStructureIsBusy(){
        return this.folderStructure.isBusy
      },

      selectedFolder() {
        let folderId = this.folderId;
        if(_.isEmpty(_.get(this.folderStructure, 'data'))) {
          return folderId;
        }

        if(folderId) {
          folderId = this.folderStructure.data.find(folder => folder.id === folderId);
        }
        else {
          folderId = this.folderStructure.data.find(folder => !folder.parent);
        }

        return folderId;
      },

      tableLoading(){
        return this.isBusy === true && this.pagination.page === 1;
      },

      loadingNextPage() {
        // return true;
        return this.isBusy === true && this.pagination.page > 1;
      },

      table () {
        const { pagination, queries, projectId, selectedFolders } = this;
        const self = this;
        // Add _folder_selected property to each query for disableKey check
        const queriesWithDisabled = queries.map(query => ({
          ...query,
          _folder_selected: selectedFolders && selectedFolders.length > 0 && selectedFolders.includes(query.folder_id)
        }));
        return {
          body: queriesWithDisabled,
          columns: [
            
            {
            key: 'checkbox',
            orderable: false,
            input: {
                type: 'checkbox',
                onChange: (value, key, row) => self.onQueryCheckboxChange(row).bind(this),
              },
              disableKey: '_folder_selected'
            },
            {
              key: 'date',
              label: self.$i18n.t('admin.headers.date'),
              orderable: true,
              render: (row) => {
                const format = 'D MMM YYYY';
                return <span class="date">{moment(row.created_at).format(format)}</span>
              }
            },
            {
              key: 'name',
              label: self.$i18n.t('admin.headers.name'),
              orderable: true,
              render: (row) => {
                let link = `/analysis/queries/${projectId}/${row.id}`
                let target = '_blank'
                if(this.isInsight) {
                  link += '?insight=true'
                  if(this.insightSessionToken) link += `&session_token=${this.insightSessionToken}`
                  target = '_self'
                }
                return <a href={link} target={target} class="headline">{row.name}</a>
              }
            },
            {
              key: 'created_by_name',
              label: self.$i18n.t('admin.headers.created_by_name'),
              orderable: true
            },
            {
              key: 'last_updated',
              label: self.$i18n.t('admin.headers.last_updated'),
              orderable: true,
              render: (row) => {
                const format = 'D MMM YYYY';
                return <span class="date">{moment(row.updated_at).format(format)}</span>
              }
            },
            {
              key: 'target',
              label: self.$i18n.t('admin.headers.target'),
              orderable: false,
              render: (row) => {
                return <span class="capitalize">{row.target.join(', ') }</span>
              }
            },
            {
              key: 'private',
              label: self.$i18n.t('admin.headers.shared'),
              orderable: false,
              render: (row) => {
                const label = row.private
                  ? self.$i18n.t('admin.is_private.no')
                  : self.$i18n.t('admin.is_private.yes'); 
                return <span class="capitalize">{ label }</span>
              }
            }
          ],
          order: {
            column: pagination.order,
            direction: pagination.sort,
            onChange: (column, direction) => {
              this.onSortTable(column, direction);
            }
          },
          addRow: {
            enabled: ((this.user && this.user.code_admin) || this.isInsight) && queries.length > 5,
            label: self.$i18n.t('admin.add_query'),
            onClick: () => self.onAddQuery()
          },
          removeRow: {
            enabled: (this.user && this.user.code_admin) || this.isInsight,
            onClick: (row) => {
              self.showPanel = 'removeQuery';
              return {
                ...self.queryModel = row
              }
            }
          }
        }
      },

      hasChildProjects () {
        return this.project.child_projects?.length > 0;
      },

      queriesProjectOptions () {
        if (!this.hasChildProjects) return [];
        return [{ value: this.project.project_id, label: this.project.project_name, default: true }, ...this.project.child_projects.map(p => ({ value: p.id, label: p.name }))];
      },

      pinnedProjects () {
        return this.preferences['qb_pinned_projects']?.ids || [];
      },

      queriesTabs () {
        if (!Array.isArray(this.pinnedProjects)) return [];
        return this.queriesProjectOptions.filter(opt => opt.default || this.pinnedProjects.indexOf(opt.value) > -1).map(opt => ({ id: opt.value, name: opt.label }));
      }
    },
    mounted () {
      this.loadPeriods();
      this.loadFolderStructure();
      this.loadMasterViewSettings();
      this.getUserPreferences({ name: 'qb_pinned_projects' });
      this.$nextTick(() => {
        this.scroll();
      });
      window.addEventListener('scroll', this.handleScroll);
    },
    beforeDestroy(){
      window.removeEventListener('scroll', this.handleScroll);
    },
    watch: {
      "query.data": {
        handler () {
          this.fetchQueries();
        },
        deep: true
      },
      "folderStructure.data": {
        handler (data) {
          this.folderId = data && !_.isEmpty(data) && data.find(folder => !folder.parent).id;
          this.fetchQueries();
        },
        deep: true
      },
      selectedProjectId (value) {
        this.getFolderStructure({ projectId: value });
        // this.fetchQueries();
      },
      "refreshQueries.error": {
        handler (data, previous) {
          if (data) {
            const error = data.response.data.message;
            if ([QUERIES.ERRORS.INVALID_MV_REQUIREMENTS, QUERIES.ERRORS.MV_NOT_FOUND].indexOf(error) > -1) {
              this.errorMessage = error;
            }
          }
        }
      },
      pagination: {
        handler(){
          this.fetchQueries();
        },
        deep: true
      },
      table() {
        this.tableVersion++
      },
      selectedFolders() {
        this.tableVersion++
      }
    },
    methods: {
      ...mapActions({
        'getQueries': 'queries/getQueries',
        'deleteQuery': 'queries/deleteQuery',
        'getFolderStructure': 'queryBuilder/folderStructure/getFolderStructure',
        'deleteFolder': 'queryBuilder/folderStructure/deleteFolder',
        'getRefreshQueries': 'queryBuilder/masterView/refresh',
        'getMasterViewSettings': 'queryBuilder/masterView/getSettings',
        'getMasterViewStatus': 'queryBuilder/masterView/getMasterViewStatus',
        'getUserPreferences': 'user/getUserPreferences',
        'updateUserPreferences': 'user/updateUserPreferences',
        'getPeriods': 'queryBuilder/periods/getPeriods',
        'bulkUpdateQueries': 'queryBuilder/query/bulkUpdateQueries',
      }),

      fetchQueries () {
        const { pagination, projectId, folderId, selectedProjectId } = this;
        if(!this.isBusy) {

          this.getQueries({
            projectId: selectedProjectId || projectId,
            pagination,
            folderId
          });
        }
      },

      loadPeriods() {
        const { projectId } = this;
        if (projectId ) {
          this.getPeriods({ projectId });
        }
      },

      closePanel: function() {
        this.showPanel = false;
        this.showForceRefreshConfirm = false;
      },

      onSortTable: function(column, direction) {
        this.pagination.order = column;
        this.pagination.sort = direction;
        this.pagination.page = 1;
        this.fetchQueries();
      },

      onAddQuery () {
        //Redirect to query builder
        const query = {}
        if(this.isInsight) {
          query.insight = true;
          query.session_token =  this.insightSessionToken;
        }
        if (this.selectedProjectId && this.selectedProjectId != this.projectId) {
          query.project_id = this.selectedProjectId;
        }
        this.$router.push({path: `/analysis/queries/${this.projectId}`, query})
      },

      onRefreshQueries (confirmed) {
        if (!confirmed) {
          this.showForceRefreshConfirm = true;
          if (this.canForceRefreshMasterView) {
            this.getMasterViewStatus({ projectId: this.projectId });
          }
        } else {
          const { projectId } = this;
          const forceRefresh = this.forceRefresh;
          if(!this.isBusy) {
            this.getRefreshQueries({ projectId, forceRefresh }).then(() => {
              this.closePanel();
              this.forceRefresh = false;
              if (forceRefresh && this.canForceRefreshMasterView) {
                this.getMasterViewStatus({ projectId });
              }
            });
          }
        }
      },

      onDeleteQuery () {
        const { projectId, queryModel, selectedProjectId } = this;
        if(!this.isBusy) {
          this.deleteQuery({
            projectId: selectedProjectId || projectId,
            queryId: queryModel.id
          });

          this.showPanel = false
        }
      },

      onSubmitSearch (value) {
        this.pagination.query = value || '';
        this.fetchQueries();
      },

      onClearSearch () {
        this.pagination.query = '';
        this.onSubmitSearch();
      },

      loadFolderStructure () {
        const {projectId} = this;
        this.getFolderStructure({projectId});
      },

      onFolderChange (value) {
        this.folderId = value;
        this.fetchQueries();
      },

      handleScroll(){
        this.scroll();
      },

      resetPagination() {
        const { pagination } = this;
        this.pagination = {
          ...pagination,
          order: 'date',
          sort: 'desc',
          page: 1
        }
      },

      scroll: function() {
        const { projectId, queries, isBusy, queriesPagination } = this;

        if (!_.isEmpty(queries) && !_.isEmpty(queriesPagination)) {
          const { currentPage, totalPages } = queriesPagination;
          if(currentPage < totalPages){
            const wHeight = window.innerHeight;
            const {top, bottom, height} = this.$refs.table.$el.getBoundingClientRect();
            const margin = 100;
            if(top * -1 + wHeight >= height - margin && isBusy == false){
              const {pagination} = this;
              this.pagination = {
                ...pagination,
                page: pagination.page + 1
              }
            }
          }
        }
      },

      onDeleteRepositoryClick (repository) {
        this.onFolderChange(repository.id);
        this.showPanel = 'deleteFolder';
      },

      onDeleteFolder(){
        this.deleteFolder({
          projectId: this.projectId,
          folderId: this.folderId
        });

        this.showPanel = false;
      },

      toggleShowEditSettingsForm (show) {
        this.showEditSettingsForm = show;
      },

      loadMasterViewSettings () {
        this.getMasterViewSettings({ projectId: this.projectId });
      },

      closeModal () {
        this.errorMessage = null;
      },

      onChangeTab (value) {
        this.selectedProjectId = value;
      },

      getElementTooltip (ref, text) {
        this.tabTooltipText = '';
        const element = this.$refs[ref][0];
        const textWidth = Math.ceil(getTextWidth(element, text));
        const padding = 30;
        const elemWidth = Math.ceil(element.getBoundingClientRect().width);
        if (textWidth >= elemWidth - padding) {
          this.tabTooltipText = text;
        }
      },

      onPinTab (value) {
        const values = [...this.pinnedProjects];
        const index = values.indexOf(value);
        if (index > -1) {
          values.splice(index, 1);
        } else {
          values.push(value);
        }
        this.updateUserPreferences({
          name: 'qb_pinned_projects',
          settings: { ids: values }
        });
      },

      isPinActive (value) {
        return this.queriesTabs.find(t => t.id === value);
      },

      toggleBulkUpdateForm(show = false) {
        this.showBulkUpdateForm = show;
      },

      onBulkUpdateQueriesDateSave(data) {
        this.bulkUpdateQueriesDateData = data;
        this.showConfirmation = true;
      },

      onUpdateSelectedFolders (folders) {
        this.selectedFolders = folders;
        // Remove queries that belong to selected folders from selectedQueries
        if (folders && folders.length > 0) {
          this.selectedQueries = this.selectedQueries.filter(queryId => {
            const query = this.queries.find(q => q.id === queryId);
            // Keep the query only if its folder is not in the selected folders
            return query && !folders.includes(query.folder_id);
          });
        }
      },

      onQueryCheckboxChange (query) {
        const index = this.selectedQueries.indexOf(query.id);
        if (index > -1) {
          this.selectedQueries.splice(index, 1);
        } else {
          this.selectedQueries.push(query.id);
        }
      },
      toggleBulkUpdateConfirmation(show = false) {
        this.showConfirmation = show;
      },
      onConfirmBulkUpdateQueriesDate () {
        const queries = {
          folder_ids: this.selectedFolders,
          queries_ids: this.selectedQueries,
          include_period: this.bulkUpdateQueriesDateData.includePeriod,
          period: { type: this.bulkUpdateQueriesDateData.periodType, range: this.bulkUpdateQueriesDateData.dateRange }
        };
        this.toggleBulkUpdateForm(false);
        this.toggleBulkUpdateConfirmation(false);
        return this.bulkUpdateQueries({ projectId: this.projectId, queries }).then((result) => {
            if (result && result.excluded && result.excluded.length > 0) {
              this.bulkUpdateExcludedResult = result;
            }
          }).finally(() => {
            this.selectedFolders = [];
            this.selectedQueries = [];
          });
      },
    },
    data () {
      return {
        pagination: {
          page: 1,
          perPage: 50,
          order: 'date',
          sort: 'desc'
        },
        showPanel: false,
        queryModel: {
          value: ''
        },
        folderId: null,
        tableVersion: 1,
        showEditSettingsForm: false,
        errorMessage: null,
        showForceRefreshConfirm: false,
        forceRefresh: false,

        selectedProjectId: +(this.$route.params.project_uid || this.$route.params.project_id),
        tabTooltipText: '',
        showBulkUpdateForm: false,
        selectedFolders: [],
        selectedQueries: [],
        showConfirmation: false,
        bulkUpdateQueriesDateData: null,
        bulkUpdateExcludedResult: null
      };
    }
  };
</script>


<style lang="scss" scoped>
  @import "../../styles/components/coding-admin/_article_queries.scss";
  @import "../../styles/components/_loader.scss";
</style>

<style lang="scss">
  .article-queries-component {
    .modal-mask {
      .modal-wrapper {
        .confirmation-modal {
          width: 30%;
        }
      }
    }
    .bulk-update-excluded-list {
      max-height: 12rem;
      overflow-y: auto;
      padding-left: 1.25rem;
    }
  }
</style>